// M4-1: server-side adapter layer for the player-centric trade workbench.
//
// The mobile trade flow is: player page "I want him" -> workbench shows the
// AI's asking price up front -> user assembles / auto-fills a package ->
// submit -> accept / counter / reject, with a hidden patience budget.
//
// This module WRAPS the existing engine primitives (makeItWork /
// team.valueChange) without changing any valuation or acceptance math. The
// asking price is generated by running makeItWork "inverted": start from the
// user receiving the target and giving nothing, and let the engine pick assets
// from the user's roster until the AI is satisfied.
//
// Return shapes are anonymous structural types (no exported aliases in the
// return position) so the RPC codegen (tools/genApiTypes.ts) expands them
// inline. All name fields are relative to the USER (give = leaves the user,
// receive = comes to the user) so a v2 reverse ("sell") flow can reuse them.

import { team } from "../index.ts";
import { idb } from "../../db/index.ts";
import { g, helpers } from "../../util/index.ts";
import { randInt, uniformSeed } from "../../../../common/random.ts";
import { PHASE } from "../../../../common/constants.ts";
import makeItWork from "./makeItWork.ts";
import get from "./get.ts";
import propose from "./propose.ts";
import summary from "./summary.ts";
import type { TradeTeams } from "../../../../common/types.ts";

// --- Tunables (documented in the M4-1 report) --------------------------------

const PATIENCE_MIN = 2;
const PATIENCE_MAX = 4;

// A re-offer counts as "materially worse" (and burns an extra patience point)
// when its user-side give value drops by more than this on the AI's valuation
// scale (roughly -0.5..2 per asset, so 0.1 is a small-but-real regression).
const WORSE_OFFER_THRESHOLD = 0.1;

// When patience is exhausted the AI walks away for this many of the user team's
// games. Cheap to support (reads the user's teamSeason games-played counter)
// and survives reload because it is stored on the trade record.
const COOLDOWN_GAMES = 3;

// --- Deterministic seeding ---------------------------------------------------

// Stable per-session seed so the initial patience is reproducible for a given
// (season, otherTid, pid). Exported so tests derive the same expected value
// rather than hard-coding it.
export const negotiationSeed = (otherTid: number, pid: number) =>
	g.get("season") * 1_000_000 + otherTid * 10_000 + pid;

export const getInitialPatience = (otherTid: number, pid: number) =>
	randInt(PATIENCE_MIN, PATIENCE_MAX, negotiationSeed(otherTid, pid));

// A per-call valueChange cache key. Same key across all the marginal
// valueChange calls inside one endpoint so the engine's cache is computed once.
const valueChangeKeyFor = (otherTid: number, pid: number) =>
	uniformSeed(negotiationSeed(otherTid, pid));

// toneLevel = f(patienceLeft): 0 cordial -> 3 final. The UI escalates wording.
const toneLevel = (patienceLeft: number) => {
	if (patienceLeft >= 3) {
		return 0;
	}
	if (patienceLeft === 2) {
		return 1;
	}
	if (patienceLeft === 1) {
		return 2;
	}
	return 3;
};

// --- Cooldown clock ----------------------------------------------------------

const userGamesPlayed = async (userTid: number) => {
	const teamSeason = await idb.cache.teamSeasons.indexGet(
		"teamSeasonsBySeasonTid",
		[g.get("season"), userTid],
	);
	return teamSeason ? helpers.getTeamSeasonGp(teamSeason) : 0;
};

const makeCooldown = async (userTid: number) => ({
	season: g.get("season"),
	gp: (await userGamesPlayed(userTid)) + COOLDOWN_GAMES,
});

const cooldownExpired = async (
	cooldown: { season: number; gp: number },
	userTid: number,
) => {
	const season = g.get("season");
	if (season !== cooldown.season) {
		// Any season change (advance or new league) clears the cooldown.
		return true;
	}
	return (await userGamesPlayed(userTid)) >= cooldown.gp;
};

// --- Asset cards -------------------------------------------------------------

const emptyTeam = (tid: number) => ({
	tid,
	pids: [] as number[],
	pidsExcluded: [] as number[],
	dpids: [] as number[],
	dpidsExcluded: [] as number[],
});

// Value that the AI assigns to a single asset changing hands, using the exact
// engine valuation (team.valueChange). Positive on both sides:
//   give    = user gives it away, AI receives it  -> +valueChange(add)
//   receive = user receives it,   AI gives it up  -> -valueChange(remove)
const buildAssets = async (
	pids: number[],
	dpids: number[],
	otherTid: number,
	side: "give" | "receive",
	valueChangeKey: number,
	userTid: number,
) => {
	const assets: {
		type: "player" | "pick";
		pid: number | null;
		dpid: number | null;
		name: string;
		value: number;
		age: number | null;
		pos: string | null;
		ovr: number | null;
		contract: { amount: number; exp: number } | null;
	}[] = [];

	for (const pid of pids) {
		const p = await idb.cache.players.get(pid);
		if (!p) {
			continue;
		}
		const value =
			side === "give"
				? await team.valueChange(
						otherTid,
						[pid],
						[],
						[],
						[],
						valueChangeKey,
						userTid,
					)
				: -(await team.valueChange(
						otherTid,
						[],
						[pid],
						[],
						[],
						valueChangeKey,
						userTid,
					));
		const ratings = p.ratings.at(-1)!;
		assets.push({
			type: "player",
			pid,
			dpid: null,
			name: `${p.firstName} ${p.lastName}`,
			value,
			age: g.get("season") - p.born.year,
			pos: ratings.pos,
			ovr: ratings.ovr,
			contract: { amount: p.contract.amount, exp: p.contract.exp },
		});
	}

	for (const dpid of dpids) {
		const dp = await idb.cache.draftPicks.get(dpid);
		if (!dp) {
			continue;
		}
		const value =
			side === "give"
				? await team.valueChange(
						otherTid,
						[],
						[],
						[dpid],
						[],
						valueChangeKey,
						userTid,
					)
				: -(await team.valueChange(
						otherTid,
						[],
						[],
						[],
						[dpid],
						valueChangeKey,
						userTid,
					));
		assets.push({
			type: "pick",
			pid: null,
			dpid,
			name: await helpers.pickDesc(dp, "short"),
			value,
			age: null,
			pos: null,
			ovr: null,
			contract: null,
		});
	}

	return assets;
};

const sumValue = (assets: { value: number }[]) =>
	assets.reduce((total, a) => total + a.value, 0);

const isBig = (pos: string | null) =>
	pos === "C" || pos === "PF" || pos === "FC" || pos === "F";

// Structured facts for the one-line GM voice. The UI writes the sentence from
// i18n templates; this only supplies the facts.
const buildHint = (
	give: Awaited<ReturnType<typeof buildAssets>>,
	totalValue: number,
) => ({
	wantsPicks: give.some((a) => a.type === "pick"),
	wantsPlayers: give.some((a) => a.type === "player"),
	wantsYoungBigs: give.some(
		(a) => a.type === "player" && a.age !== null && a.age <= 25 && isBig(a.pos),
	),
	numAssets: give.length,
	totalValue,
});

const dvFor = (
	otherTid: number,
	giveTeam: { pids: number[]; dpids: number[] },
	receiveTeam: { pids: number[]; dpids: number[] },
	valueChangeKey: number,
	userTid: number,
) =>
	team.valueChange(
		otherTid,
		giveTeam.pids,
		receiveTeam.pids,
		giveTeam.dpids,
		receiveTeam.dpids,
		valueChangeKey,
		userTid,
	);

type OfferArg = {
	pid: number;
	give: { pids: number[]; dpids: number[] };
	receive: { pids: number[]; dpids: number[] };
};

const teamsForOffer = (
	userTid: number,
	otherTid: number,
	arg: OfferArg,
): TradeTeams => [
	{
		tid: userTid,
		pids: [...arg.give.pids],
		pidsExcluded: [],
		dpids: [...arg.give.dpids],
		dpidsExcluded: [],
	},
	{
		tid: otherTid,
		pids: [...arg.receive.pids],
		pidsExcluded: [],
		dpids: [...arg.receive.dpids],
		dpidsExcluded: [],
	},
];

const ruleViolationReason = () =>
	g.get("salaryCapType") === "hard"
		? ("hard_cap" as const)
		: ("salary_match" as const);

const sameIDs = (a: number[], b: number[]) => {
	if (a.length !== b.length) {
		return false;
	}
	const sortedA = [...a].sort((x, y) => x - y);
	const sortedB = [...b].sort((x, y) => x - y);
	return sortedA.every((id, index) => id === sortedB[index]);
};

const sameStoredOffer = (stored: TradeTeams, expected: TradeTeams) =>
	stored[0].tid === expected[0].tid &&
	stored[1].tid === expected[1].tid &&
	sameIDs(stored[0].pids, expected[0].pids) &&
	sameIDs(stored[0].dpids, expected[0].dpids) &&
	sameIDs(stored[1].pids, expected[1].pids) &&
	sameIDs(stored[1].dpids, expected[1].dpids);

// =============================================================================
// 1. getTradeQuote(pid) — target player on another team.
// =============================================================================
export const getTradeQuote = async (pid: number) => {
	const userTid = g.get("userTid");
	const target = await idb.cache.players.get(pid);
	if (!target) {
		return { ok: false as const, errorHint: "player_not_found" as const };
	}
	const otherTid = target.tid;
	if (otherTid === userTid || otherTid < 0) {
		return { ok: false as const, errorHint: "not_on_other_team" as const };
	}

	const valueChangeKey = valueChangeKeyFor(otherTid, pid);

	// Inverted makeItWork: user receives the target and gives nothing; the
	// engine adds assets from the user's roster until the AI accepts. Because
	// the deal starts unfavourable to the AI, makeItWork only ever adds
	// user-side assets (AI-side assets move value the wrong way and are filtered
	// out inside tryAddAsset), so the result IS the AI's asking price.
	const start: TradeTeams = [emptyTeam(userTid), { ...emptyTeam(otherTid), pids: [pid] }];
	const result = await makeItWork(helpers.deepCopy(start), {
		holdUserConstant: false,
		valueChangeKey,
	});

	// Explicit "no acceptable package" semantics (M4-2 fixup): makeItWork is
	// all-or-nothing — when NO combination of the user's tradable assets reaches
	// dv > 0 it returns undefined, never a partial package. That happens both
	// when the roster genuinely cannot cover the target's value AND when a phase
	// gate shrinks the candidate pool (isUntradable rejects every expiring
	// contract for PLAYOFFS < phase < FREE_AGENCY, i.e. the whole draft window).
	// Surface it as attainable:false with an EMPTY ask and the raw deficit kept
	// in beam.gap. The UI must render an honest "they won't budge" state on
	// attainable:false — a zero-asset asking price must never be presented as a
	// real quote.
	const askTeams = result ?? start;
	const attainable = result !== undefined;

	const give = await buildAssets(
		askTeams[0].pids,
		askTeams[0].dpids,
		otherTid,
		"give",
		valueChangeKey,
		userTid,
	);
	const receive = await buildAssets(
		askTeams[1].pids,
		askTeams[1].dpids,
		otherTid,
		"receive",
		valueChangeKey,
		userTid,
	);

	const giveValue = sumValue(give);
	const receiveValue = sumValue(receive);
	const gap = await dvFor(
		otherTid,
		{ pids: askTeams[0].pids, dpids: askTeams[0].dpids },
		{ pids: askTeams[1].pids, dpids: askTeams[1].dpids },
		valueChangeKey,
		userTid,
	);

	const targetRatings = target.ratings.at(-1)!;
	return {
		ok: true as const,
		userTid,
		otherTid,
		pid,
		target: {
			pid,
			name: `${target.firstName} ${target.lastName}`,
			age: g.get("season") - target.born.year,
			pos: targetRatings.pos,
			ovr: targetRatings.ovr,
			value: target.value,
			contract: { amount: target.contract.amount, exp: target.contract.exp },
		},
		// attainable:false ⇔ ask.give is empty by construction (see the makeItWork
		// comment above); beam.gap then carries how far short the empty offer is.
		attainable,
		ask: {
			give,
			totalValue: giveValue,
			willAccept: attainable,
		},
		hint: buildHint(give, giveValue),
		beam: { giveValue, receiveValue, gap, willAccept: attainable },
	};
};

// =============================================================================
// 2. suggestTradePackage(pid, currentAssets) — one-tap top-up to acceptance.
// =============================================================================
export const suggestTradePackage = async (arg: {
	pid: number;
	pids: number[];
	dpids: number[];
}) => {
	// Single object param: the single-param toWorker wire cannot carry a
	// positional second argument, so the UI-facing endpoints take one object.
	const { pid } = arg;
	const currentAssets = { pids: arg.pids, dpids: arg.dpids };
	const userTid = g.get("userTid");
	const target = await idb.cache.players.get(pid);
	if (!target) {
		return { ok: false as const, errorHint: "player_not_found" as const };
	}
	const otherTid = target.tid;
	if (otherTid === userTid || otherTid < 0) {
		return { ok: false as const, errorHint: "not_on_other_team" as const };
	}

	const valueChangeKey = valueChangeKeyFor(otherTid, pid);

	const currentPids = currentAssets.pids.slice();
	const currentDpids = currentAssets.dpids.slice();

	const start: TradeTeams = [
		{ ...emptyTeam(userTid), pids: currentPids.slice(), dpids: currentDpids.slice() },
		{ ...emptyTeam(otherTid), pids: [pid] },
	];

	const dv0 = await dvFor(
		otherTid,
		{ pids: currentPids, dpids: currentDpids },
		{ pids: [pid], dpids: [] },
		valueChangeKey,
		userTid,
	);

	// Already good enough for the AI -> keep the user's package untouched (do
	// not let the AI sweeten its own side). Otherwise top up the user side with
	// the minimal extra value makeItWork finds.
	let finalTeams = start;
	if (dv0 <= 0) {
		finalTeams =
			(await makeItWork(helpers.deepCopy(start), {
				holdUserConstant: false,
				valueChangeKey,
			})) ?? start;
	}

	const give = await buildAssets(
		finalTeams[0].pids,
		finalTeams[0].dpids,
		otherTid,
		"give",
		valueChangeKey,
		userTid,
	);
	const receive = await buildAssets(
		finalTeams[1].pids,
		finalTeams[1].dpids,
		otherTid,
		"receive",
		valueChangeKey,
		userTid,
	);

	const gap = await dvFor(
		otherTid,
		{ pids: finalTeams[0].pids, dpids: finalTeams[0].dpids },
		{ pids: finalTeams[1].pids, dpids: finalTeams[1].dpids },
		valueChangeKey,
		userTid,
	);

	const added = give.filter(
		(a) =>
			(a.type === "player" && !currentPids.includes(a.pid!)) ||
			(a.type === "pick" && !currentDpids.includes(a.dpid!)),
	);

	// willAccept:false together with added:[] means the top-up FAILED: even
	// maxing out the user's tradable assets, makeItWork found no acceptable
	// package (the same all-or-nothing semantics documented on getTradeQuote's
	// attainable flag — finalTeams fell back to the unchanged start). The UI
	// must surface explicit feedback on that combination instead of silently
	// applying an unchanged package.
	return {
		ok: true as const,
		userTid,
		otherTid,
		pid,
		suggested: { give, receive },
		added,
		beam: {
			giveValue: sumValue(give),
			receiveValue: sumValue(receive),
			gap,
		},
		willAccept: gap > 0,
	};
};

// =============================================================================
// 2b. previewTradeOffer(pid, assets) — read-only live beam during assembly.
// =============================================================================
// A pure valueChange wrapper (same buildAssets/dvFor path as the other
// endpoints) that reports the balance beam for the EXACT current package. It
// does NOT touch the negotiation record or burn any patience, so the UI can call
// it on every add/remove while the user assembles their offer. The per-asset
// give/receive breakdown is returned alongside the beam because computing the
// beam already sums those asset values, so the UI can label every row (each
// asset "with value") without a second round-trip.
export const previewTradeOffer = async (arg: OfferArg) => {
	const { pid } = arg;
	const assets = { give: arg.give, receive: arg.receive };
	const userTid = g.get("userTid");
	const target = await idb.cache.players.get(pid);
	if (!target) {
		return { ok: false as const, errorHint: "player_not_found" as const };
	}
	const otherTid = target.tid;
	if (otherTid === userTid || otherTid < 0) {
		return { ok: false as const, errorHint: "not_on_other_team" as const };
	}

	const valueChangeKey = valueChangeKeyFor(otherTid, pid);

	const give = await buildAssets(
		assets.give.pids,
		assets.give.dpids,
		otherTid,
		"give",
		valueChangeKey,
		userTid,
	);
	const receive = await buildAssets(
		assets.receive.pids,
		assets.receive.dpids,
		otherTid,
		"receive",
		valueChangeKey,
		userTid,
	);
	const giveValue = sumValue(give);
	const receiveValue = sumValue(receive);
	const gap = await dvFor(
		otherTid,
		assets.give,
		assets.receive,
		valueChangeKey,
		userTid,
	);

	return {
		ok: true as const,
		userTid,
		otherTid,
		pid,
		give,
		receive,
		beam: { giveValue, receiveValue, gap, willAccept: gap > 0 },
	};
};

// =============================================================================
// 3. evaluateTradeOffer(pid, assets) — submit path + patience state machine.
// =============================================================================
export const evaluateTradeOffer = async (arg: OfferArg) => {
	const { pid } = arg;
	const assets = { give: arg.give, receive: arg.receive };
	const userTid = g.get("userTid");
	const target = await idb.cache.players.get(pid);
	if (!target) {
		return {
			outcome: "reject" as const,
			reasonHint: "player_not_found" as const,
		};
	}
	const otherTid = target.tid;
	if (otherTid === userTid || otherTid < 0) {
		return {
			outcome: "reject" as const,
			reasonHint: "not_on_other_team" as const,
		};
	}

	// Acceptance and league legality are separate facts. The deterministic beam
	// only answers whether the other GM likes the value; it cannot override cap
	// matching. Reject illegal packages before opening/burning the patience state
	// so the UI never presents a false "They accepted" confirmation.
	const rules = await summary(teamsForOffer(userTid, otherTid, arg));
	if (rules.warning) {
		return {
			outcome: "reject" as const,
			reasonHint: ruleViolationReason(),
			warningAmount: rules.warningAmount,
		};
	}

	const valueChangeKey = valueChangeKeyFor(otherTid, pid);

	// Load / (re)initialise the negotiation session on the singleton trade
	// record. A change of (userTid, otherTid, pid) starts a fresh session.
	const tr = await get();
	let neg = tr.negotiation;
	if (
		!neg ||
		neg.userTid !== userTid ||
		neg.otherTid !== otherTid ||
		neg.pid !== pid
	) {
		const patienceInit = getInitialPatience(otherTid, pid);
		neg = {
			userTid,
			otherTid,
			pid,
			patienceInit,
			patienceLeft: patienceInit,
			round: 0,
			lastGiveValue: null,
			cooldown: null,
		};
	}

	const persist = async () => {
		tr.negotiation = neg;
		await idb.cache.trade.put(tr);
	};

	// Cooldown gate: the AI has walked away until the clock passes.
	if (neg.cooldown && !(await cooldownExpired(neg.cooldown, userTid))) {
		await persist();
		return {
			outcome: "reject" as const,
			reasonHint: "cooldown" as const,
			patienceLeft: neg.patienceLeft,
			toneLevel: toneLevel(neg.patienceLeft),
			cooldown: neg.cooldown,
		};
	}
	// Cooldown expired -> the session is over; fall through as a fresh one.
	if (neg.cooldown) {
		const patienceInit = getInitialPatience(otherTid, pid);
		neg = {
			userTid,
			otherTid,
			pid,
			patienceInit,
			patienceLeft: patienceInit,
			round: 0,
			lastGiveValue: null,
			cooldown: null,
		};
	}

	const give = await buildAssets(
		assets.give.pids,
		assets.give.dpids,
		otherTid,
		"give",
		valueChangeKey,
		userTid,
	);
	const receive = await buildAssets(
		assets.receive.pids,
		assets.receive.dpids,
		otherTid,
		"receive",
		valueChangeKey,
		userTid,
	);
	const giveValue = sumValue(give);
	const receiveValue = sumValue(receive);
	const gap = await dvFor(
		otherTid,
		assets.give,
		assets.receive,
		valueChangeKey,
		userTid,
	);
	const beam = { giveValue, receiveValue, gap };

	// Accept: the AI is satisfied. We do NOT execute the trade here; execution
	// stays behind the explicit, independently validated confirmTradeOffer RPC.
	if (gap > 0) {
		neg.cooldown = null;
		tr.negotiation = undefined;
		await idb.cache.trade.put(tr);
		return {
			outcome: "accept" as const,
			beam,
			patienceLeft: neg.patienceLeft,
		};
	}

	// Not good enough -> burn patience. Extra burn if the re-offer got
	// materially worse than the previous one.
	const worse =
		neg.lastGiveValue !== null &&
		giveValue < neg.lastGiveValue - WORSE_OFFER_THRESHOLD;
	neg.patienceLeft -= 1 + (worse ? 1 : 0);
	neg.round += 1;
	neg.lastGiveValue = giveValue;

	// Exhausted -> reject and start a cooldown.
	if (neg.patienceLeft <= 0) {
		neg.patienceLeft = 0;
		neg.cooldown = await makeCooldown(userTid);
		await persist();
		return {
			outcome: "reject" as const,
			reasonHint: "patience_exhausted" as const,
			patienceLeft: 0,
			toneLevel: toneLevel(0),
			cooldown: neg.cooldown,
			worse,
			beam,
		};
	}

	// Still patient -> counter. Run makeItWork with the user's current offer
	// locked in to find the diff that WOULD work.
	const start: TradeTeams = [
		{
			...emptyTeam(userTid),
			pids: assets.give.pids.slice(),
			dpids: assets.give.dpids.slice(),
		},
		{
			...emptyTeam(otherTid),
			pids: assets.receive.pids.slice(),
			dpids: assets.receive.dpids.slice(),
		},
	];
	const worked = await makeItWork(helpers.deepCopy(start), {
		holdUserConstant: false,
		valueChangeKey,
	});

	if (!worked) {
		// No package works even maxed out. Patience already burned this round.
		await persist();
		return {
			outcome: "reject" as const,
			reasonHint: "too_lopsided" as const,
			patienceLeft: neg.patienceLeft,
			toneLevel: toneLevel(neg.patienceLeft),
			worse,
			beam,
		};
	}

	const counterGive = await buildAssets(
		worked[0].pids,
		worked[0].dpids,
		otherTid,
		"give",
		valueChangeKey,
		userTid,
	);
	const counterReceive = await buildAssets(
		worked[1].pids,
		worked[1].dpids,
		otherTid,
		"receive",
		valueChangeKey,
		userTid,
	);
	const addGive = counterGive.filter(
		(a) =>
			(a.type === "player" && !assets.give.pids.includes(a.pid!)) ||
			(a.type === "pick" && !assets.give.dpids.includes(a.dpid!)),
	);
	const counterGap = await dvFor(
		otherTid,
		{ pids: worked[0].pids, dpids: worked[0].dpids },
		{ pids: worked[1].pids, dpids: worked[1].dpids },
		valueChangeKey,
		userTid,
	);

	await persist();
	return {
		outcome: "counter" as const,
		counter: {
			give: counterGive,
			receive: counterReceive,
			addGive,
			patienceLeft: neg.patienceLeft,
			toneLevel: toneLevel(neg.patienceLeft),
		},
		beam: {
			giveValue: sumValue(counterGive),
			receiveValue: sumValue(counterReceive),
			gap: counterGap,
		},
	};
};

// =============================================================================
// 4. confirmTradeOffer(pid, assets) — execute one evaluated workbench offer.
// =============================================================================
// This is deliberately the only bridge to propose({ acceptedByWorkbench:true }).
// The internal flag is never accepted from an RPC caller. We independently
// re-check the stored offer, phase, league rules, and the same deterministic
// valueChange key used by evaluateTradeOffer before skipping legacy AI
// revaluation. A changed or newly-illegal offer fails visibly and does not move
// any assets.
export const confirmTradeOffer = async (arg: OfferArg) => {
	const userTid = g.get("userTid");
	const target = await idb.cache.players.get(arg.pid);
	if (!target) {
		return { ok: false as const, reasonHint: "player_not_found" as const };
	}
	const otherTid = target.tid;
	if (otherTid === userTid || otherTid < 0) {
		return { ok: false as const, reasonHint: "not_on_other_team" as const };
	}
	if (
		g.get("phase") >= PHASE.AFTER_TRADE_DEADLINE &&
		g.get("phase") <= PHASE.PLAYOFFS
	) {
		return { ok: false as const, reasonHint: "phase_closed" as const };
	}

	const expected = teamsForOffer(userTid, otherTid, arg);
	const stored = await get();
	if (!sameStoredOffer(stored.teams, expected)) {
		return { ok: false as const, reasonHint: "offer_changed" as const };
	}

	const rules = await summary(stored.teams);
	if (rules.warning) {
		return {
			ok: false as const,
			reasonHint: ruleViolationReason(),
			warningAmount: rules.warningAmount,
		};
	}

	const gap = await dvFor(
		otherTid,
		arg.give,
		arg.receive,
		valueChangeKeyFor(otherTid, arg.pid),
		userTid,
	);
	if (gap <= 0) {
		return {
			ok: false as const,
			reasonHint: "no_longer_accepted" as const,
			gap,
		};
	}

	const [accepted, message] = await propose({ acceptedByWorkbench: true });
	if (!accepted) {
		return {
			ok: false as const,
			reasonHint: "execution_failed" as const,
		};
	}

	return { ok: true as const, message };
};
